import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { Logo } from 'components/Logo';
import { Button, Icon, Input } from 'components/ui';
import CirculerShadow from 'components/ui/CirculerShadow';
import { BACK_TO_WEBSITE_LINK } from 'config/constants';

export const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const { error } = await signIn(email, password);

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center bg-gray-100">
      <div
        className="w-full justify-between fixed top-0 max-w-7xl mx-auto z-10 flex pt-6 px-6"
        onClick={() => {
          window.open(BACK_TO_WEBSITE_LINK, '_blank');
        }}
      >
        <Logo />
        <a
          href={BACK_TO_WEBSITE_LINK}
          className="text-sm font-medium text-gray-600 hover:text-primary transition-colors"
        >
          ← Back to Website
        </a>
      </div>

      <div className="my-4 py-6 text-center">
        <h1 className="text-4xl font-medium">Welcome back !</h1>
        <p className="text-xs">Log in to access your AI Marketing dashboard</p>
      </div>
      <div className="bg-white p-8 rounded-lg shadow-md w-96">
        <form className="space-y-3" onSubmit={handleSubmit}>
          <Input
            type="email"
            label="Email"
            name="email"
            placeholder="Email"
            onChange={(e) => setEmail(e.target.value)}
            value={email}
            required
            disabled={loading}
          />
          <div>
            <Input
              type="password"
              label="Password"
              name="password"
              placeholder="Password"
              onChange={(e) => setPassword(e.target.value)}
              value={password}
              required
              disabled={loading}
            />
            <div className="text-right mt-1">
              <Link to="/reset-password" className="text-xs text-link hover:underline">
                Forgot password?
              </Link>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">{error}</div>
          )}

          <Button type="submit" className="mt-6" variant="primary" size="sm" disabled={loading}>
            {loading ? 'Logging in...' : 'Log in'}
          </Button>
        </form>
        <div className="flex items-center my-4">
          <div className="flex-grow border-t border-gray-300"></div>
          <span className="mx-4 text-xs text-gray-500">or</span>
          <div className="flex-grow border-t border-gray-300"></div>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant="outline"
            loading={loading}
            size="sm"
            disabled={loading}
            className="shadow-none flex gap-2 items-center"
          >
            <Icon type="google" className="w-4" /> Continue with Google
          </Button>
          <Button
            type="button"
            variant="outline"
            loading={loading}
            size="sm"
            disabled={loading}
            className="shadow-none flex gap-2 items-center"
          >
            <Icon type="linkedin" className="w-4" /> Continue with Linkedin
          </Button>
        </div>

        <p className="mt-4 text-center text-xs text-gray-600 flex justify-center items-center gap-2">
          Don't have an account?{' '}
          <Link
            to="/signup"
            className="text-link font-medium underline flex gap-1 items-center justify-center"
          >
            Sign up <Icon type="rightArrow" className="w-3 h-3 font-thin" />
          </Link>
        </p>
      </div>

      <div className="md:absolute hidden -top-30 right-0 ">
        <CirculerShadow className="" />
      </div>
    </div>
  );
};
